import { useLazyQuery } from '@apollo/client';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { Box, Button, Popover, Typography, useTheme } from '@mui/material';
import { Loader } from '@prismamedia/one-components';
import {
  GetSuggestionOpenAiQuery,
  GetSuggestionOpenAiQueryVariables,
} from '__generated__/queries-newsletter';
import { GET_SUGGESTION_OPEN_AI_QUERY } from 'apollo/queries/suggestionOpenAi.newsletter.graphql';
import { ContentState, convertToRaw } from 'draft-js';
import { FC, MouseEvent, useState } from 'react';

type FieldsType = 'subject' | 'preHeader';

interface FieldsDataType {
  onConfirm: (value: string) => void;
  value: string;
}

interface ContainerSuggestionAiProps {
  title?: string;
  disabled: boolean;
  fieldsData: Record<FieldsType, FieldsDataType>;
  prompt: string;
}

export const ContainerSuggestionAi: FC<ContainerSuggestionAiProps> = ({
  title,
  children,
  disabled,
  fieldsData,
  prompt,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const [getSuggestionOpenAi, { loading, error }] = useLazyQuery<
    GetSuggestionOpenAiQuery,
    GetSuggestionOpenAiQueryVariables
  >(GET_SUGGESTION_OPEN_AI_QUERY, {
    onError: (err) => err.message,
  });
  const errorMessage = "Erreur: La suggestion n'a pas pu etre générée.";

  const handleSuggestion = async () => {
    if (!disabled) {
      const { data: suggestion } = await getSuggestionOpenAi({
        variables: { prompt },
      });
      if (suggestion?.getSuggestionOpenAi) {
        const parsedResults = JSON.parse(suggestion.getSuggestionOpenAi);
        parsedResults.preHeader &&
          fieldsData.preHeader.onConfirm(
            JSON.stringify(
              convertToRaw(
                ContentState.createFromText(parsedResults.preHeader),
              ),
            ),
          );
        parsedResults.subject &&
          fieldsData.subject.onConfirm(
            JSON.stringify(
              convertToRaw(ContentState.createFromText(parsedResults.subject)),
            ),
          );
      }
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        padding: '20px',
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: '20px',
      }}
    >
      {title && (
        <Box
          sx={{
            display: 'flex',
            position: 'absolute',
            top: -17,
            backgroundColor: theme.palette.common.white,
          }}
        >
          <Typography
            sx={{
              marginLeft: '10px',
            }}
            variant="h6"
          >
            {title}
          </Typography>
          {disabled && (
            <Popover
              id="mouse-over-popover"
              sx={{
                pointerEvents: 'none',
              }}
              open={open}
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'center',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'center',
                horizontal: 'left',
              }}
              onClose={handlePopoverClose}
              disableRestoreFocus
            >
              <Typography sx={{ py: 1, px: 2 }}>
                Veuillez ajouter du contenu
              </Typography>
            </Popover>
          )}

          <Button
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
            onClick={handleSuggestion}
            sx={{
              cursor: `${disabled ? 'auto' : 'pointer'}`,
              position: 'relative',
              height: '30px',
              width: '32px',
              minWidth: '32px',
              marginRight: '10px',
              marginLeft: '15px',
              opacity: disabled ? '40%' : 1,
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
              },
              borderRadius: '4px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {loading ? (
              <Loader color="secondary" size={20} />
            ) : (
              <AutoFixHighIcon
                sx={{
                  fill: theme.palette.common.white,
                  padding: '1px',
                }}
              ></AutoFixHighIcon>
            )}
          </Button>
          {error && <Typography sx={{ ml: 8 }}>{errorMessage}</Typography>}
        </Box>
      )}
      {children}
    </Box>
  );
};
